import { useState, useMemo, useCallback } from 'react';
import { type AppState, type RankingItem } from './types';
import { ItemInput } from './components/ItemInput';
import { ComparisonView } from './components/ComparisonView';
import { ResultsView } from './components/ResultsView';
import { updateRatings, generateComparisons } from './utils/ranking';
import { BarChart3 } from 'lucide-react';
import ToolWrapper, { type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { type ToolItem } from '../../contexts/toolItemsHooks';

const INITIAL_SCORE = 1000; // Starting Elo rating

function PairwiseRankingEloContent() {
  const [state, setState] = useState<AppState>('input');
  const [itemNames, setItemNames] = useState<string[]>([]);
  const [items, setItems] = useState<RankingItem[]>([]);
  const [comparisons, setComparisons] = useState<Array<[string, string]>>([]);
  const [currentComparisonIndex, setCurrentComparisonIndex] = useState(0);
  const [, setCompletedComparisons] = useState<Set<string>>(new Set());
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newNames = toolItems.map(item => item.name);
    setItemNames(prev => [...prev, ...newNames]);
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach((item, idx) => {
      const itemId = `item-${itemNames.length + idx}`;
      if (item.sourceId) {
        newSourceIdMap[itemId] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
  }, [itemNames.length]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Sort by Elo score (highest first)
    const sortedItems = [...items].sort((a, b) => b.score - a.score);
    
    return sortedItems.map((item, idx) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      rank: idx + 1,
      score: item.score,
      metadata: {
        eloScore: item.score,
        wins: item.wins,
        losses: item.losses,
        comparisons: item.comparisons,
      }
    }));
  }, [items, sourceIdMap]);

  // Initialize items when starting comparison
  const handleStart = () => {
    const newItems: RankingItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      score: INITIAL_SCORE,
      wins: 0,
      losses: 0,
      comparisons: 0,
    }));

    const allComparisons = generateComparisons(newItems);
    
    setItems(newItems);
    setComparisons(allComparisons);
    setCurrentComparisonIndex(0);
    setCompletedComparisons(new Set());
    setState('comparing');
  };

  // Handle comparison selection
  const handleComparison = (winnerId: string) => {
    const [item1Id, item2Id] = comparisons[currentComparisonIndex];
    const item1 = items.find((i) => i.id === item1Id)!;
    const item2 = items.find((i) => i.id === item2Id)!;

    const { item1: updatedItem1, item2: updatedItem2 } = updateRatings(
      item1,
      item2,
      winnerId
    );

    // Update items
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === item1Id) return updatedItem1;
        if (item.id === item2Id) return updatedItem2;
        return item;
      })
    );

    // Mark this comparison as completed
    const comparisonKey = `${item1Id}-${item2Id}`;
    setCompletedComparisons((prev) => new Set(prev).add(comparisonKey));

    // Move to next comparison or show results
    if (currentComparisonIndex < comparisons.length - 1) {
      setCurrentComparisonIndex((prev) => prev + 1);
    } else {
      setState('results');
    }
  };

  // Reset to start over
  const handleReset = () => {
    setState('input');
    setItemNames([]);
    setItems([]);
    setComparisons([]);
    setCurrentComparisonIndex(0);
    setCompletedComparisons(new Set());
    setSourceIdMap({});
  };

  // Get current comparison items
  const currentComparison = useMemo(() => {
    if (comparisons.length === 0 || currentComparisonIndex >= comparisons.length) {
      return null;
    }
    const [item1Id, item2Id] = comparisons[currentComparisonIndex];
    const item1 = items.find((i) => i.id === item1Id);
    const item2 = items.find((i) => i.id === item2Id);
    return item1 && item2 ? { item1, item2 } : null;
  }, [comparisons, currentComparisonIndex, items]);

  const progress =
    comparisons.length > 0
      ? ((currentComparisonIndex + 1) / comparisons.length) * 100
      : 0;

  return (
    <ToolWrapper
      toolName="Pairwise Ranking (Elo)"
      toolDescription="Compare items two at a time using Elo ratings"
      onImportItems={handleImportItems}
      onGetResults={state === 'results' ? handleGetResults : undefined}
    >
      <div className="w-full">
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-[var(--rule)]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <BarChart3 className="h-8 w-8 text-[var(--text-ter)] flex-shrink-0" />
              <div>
                <h1 className="text-2xl font-bold text-[var(--text-pri)]">
                  Pairwise Ranking (Elo)
                </h1>
                <p className="text-sm text-[var(--text-sec)] mt-1">
                  Compare items two at a time using the Elo rating system. Each comparison updates ratings dynamically.
                </p>
              </div>
            </div>
            {state !== 'input' && (
              <button
                onClick={handleReset}
                className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] whitespace-nowrap"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div>
          {state === 'input' && (
            <ItemInput
              items={itemNames}
              onItemsChange={setItemNames}
              onStart={handleStart}
            />
          )}

          {state === 'comparing' && currentComparison && (
            <ComparisonView
              item1={currentComparison.item1}
              item2={currentComparison.item2}
              onSelect={handleComparison}
              progress={progress}
              totalComparisons={comparisons.length}
              currentComparison={currentComparisonIndex + 1}
            />
          )}

          {state === 'results' && (
            <ResultsView items={items} onReset={handleReset} />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function PairwiseRanking() {
  return <PairwiseRankingEloContent />;
}
